import React, { useEffect, useState, useCallback, createContext, useContext, useMemo } from 'react';
import './M3Snackbar.css';

/**
 * Global snackbar context — useSnackbar() anywhere for a scoped instance,
 * or call snackbar.show()/snackbar.success()/etc. for a global one-shot call.
 */
export const SnackbarContext = createContext(null);

export const snackbar = {
  _show: null,
  show(message, options) {
    if (this._show) this._show(message, options);
  },
  success(message, options) {
    this.show(message, { ...options, type: 'success' });
  },
  error(message, options) {
    this.show(message, { ...options, type: 'error' });
  },
  warning(message, options) {
    this.show(message, { ...options, type: 'warning' });
  },
  info(message, options) {
    this.show(message, { ...options, type: 'info' });
  }
};

export function SnackbarProvider({ children }) {
  const [snackbars, setSnackbars] = useState([]);

  const show = useCallback((message, options = {}) => {
    const id = nextId++;
    const { type = 'info', actionLabel, onAction, duration = 4000, dismissible = true } = options;

    setSnackbars((prev) => [
      ...prev,
      { id, message, type, actionLabel, onAction, duration, dismissible }
    ]);

    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }

    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setSnackbars((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      snackbars,
      show,
      dismiss,
      success: (m, o) => show(m, { ...o, type: 'success' }),
      error: (m, o) => show(m, { ...o, type: 'error' }),
      warning: (m, o) => show(m, { ...o, type: 'warning' }),
      info: (m, o) => show(m, { ...o, type: 'info' })
    }),
    [snackbars, show, dismiss]
  );

  // Register on global snackbar object
  useEffect(() => {
    snackbar._show = show;
  }, [show]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <M3SnackbarContainer snackbars={snackbars} onDismiss={dismiss} />
    </SnackbarContext.Provider>
  );
}

let nextId = 0;

export function useSnackbar() {
  const ctx = useContext(SnackbarContext);
  if (!ctx) return useSnackbarIsolated();
  return ctx;
}

function useSnackbarIsolated() {
  const [snackbars, setSnackbars] = useState([]);

  const show = useCallback((message, options = {}) => {
    const id = nextId++;
    const { type = 'info', actionLabel, onAction, duration = 4000, dismissible = true } = options;

    setSnackbars((prev) => [
      ...prev,
      { id, message, type, actionLabel, onAction, duration, dismissible }
    ]);

    if (duration > 0) {
      setTimeout(() => dismiss(id), duration);
    }

    return id;
  }, []);

  const dismiss = useCallback((id) => {
    setSnackbars((prev) => prev.filter((s) => s.id !== id));
  }, []);

  return {
    snackbars,
    show,
    dismiss,
    success: (m, o) => show(m, { ...o, type: 'success' }),
    error: (m, o) => show(m, { ...o, type: 'error' }),
    warning: (m, o) => show(m, { ...o, type: 'warning' }),
    info: (m, o) => show(m, { ...o, type: 'info' })
  };
}

function M3SnackbarContainer({ snackbars, onDismiss }) {
  return (
    <div
      className="m3-snackbar-surface"
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      {snackbars.map((snackbar) => (
        <M3Snackbar key={snackbar.id} {...snackbar} onDismiss={() => onDismiss(snackbar.id)} />
      ))}
    </div>
  );
}

function M3Snackbar({ message, type, actionLabel, onAction, dismissible, onDismiss }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    setTimeout(onDismiss, 200);
  };

  const icons = {
    success: 'check_circle',
    error: 'error',
    warning: 'warning',
    info: 'info'
  };

  return (
    <div
      className={`m3-snackbar m3-snackbar-${type} ${visible ? 'm3-snackbar-visible' : ''}`}
      role="status"
    >
      <span className="material-symbols-rounded m3-snackbar-icon">{icons[type] || icons.info}</span>
      <span className="m3-snackbar-message">{message}</span>
      <div className="m3-snackbar-actions">
        {actionLabel && (
          <button
            className="m3-snackbar-action"
            onClick={() => {
              onAction?.();
              handleDismiss();
            }}
          >
            {actionLabel}
          </button>
        )}
        {dismissible && (
          <button className="m3-snackbar-dismiss" onClick={handleDismiss} aria-label="Dismiss">
            <span className="material-symbols-rounded">close</span>
          </button>
        )}
      </div>
    </div>
  );
}
